import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook for managing URL search parameters
 * @returns {object} Search params utilities
 */
export default function useSearchParams() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  
  /**
   * Get a search parameter value
   * @param {string} key - Parameter key
   * @returns {string|null} Parameter value
   */
  const getParam = useCallback((key) => {
    return searchParams.get(key);
  }, [searchParams]);
  
  /**
   * Set a search parameter value
   * @param {string} key - Parameter key
   * @param {string} value - Parameter value
   */
  const setParam = useCallback((key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    navigate({ search: newParams.toString() }, { replace: true });
  }, [navigate, searchParams]);
  
  /**
   * Remove a search parameter
   * @param {string} key - Parameter key
   */
  const removeParam = useCallback((key) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    navigate({ search: newParams.toString() }, { replace: true });
  }, [navigate, searchParams]);
  
  /**
   * Set multiple search parameters at once
   * @param {object} params - Object with key-value pairs
   */
  const setMultipleParams = useCallback((params) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    navigate({ search: newParams.toString() }, { replace: true });
  }, [navigate, searchParams]);
  
  /**
   * Clear all search parameters
   */
  const clearParams = useCallback(() => {
    navigate({ search: '' }, { replace: true });
  }, [navigate]);
  
  /**
   * Check if a parameter exists
   * @param {string} key - Parameter key
   * @returns {boolean} True if parameter exists
   */
  const hasParam = useCallback((key) => {
    return searchParams.has(key);
  }, [searchParams]);
  
  return {
    searchParams,
    getParam,
    setParam,
    removeParam,
    setMultipleParams,
    clearParams,
    hasParam
  };
}
